import { useQuery } from "@tanstack/react-query";

// ─── Types ──────────────────────────────────────────────────────────────────

export interface Product {
  id: number;
  name: string;
  slug: string;
  category: string | null;
  price: string | null;
  imageUrl: string | null;
  richDesc: string | null;
  features: string[];
  benefits: string[];
  url: string | null;
}

export interface Asset {
  id: number;
  offerId: number;
  sourceUrl: string | null;
  previewUrl: string | null;
  localPath: string | null;
  filename: string | null;
  mimeType: string | null;
  width: number | null;
  height: number | null;
  qualityScore: number | null;
  shotType: string | null;
  description: string | null;
}

export interface BlogPost {
  id: number;
  title: string;
  slug: string;
  heroImagePath: string | null;
  publishedAt: string | null;
}

export interface PricingBundle {
  name?: string;
  price?: string;
  description?: string;
}

export interface ProductResearch {
  features: unknown;
  benefits: unknown;
  pricing: {
    price?: string;
    originalPrice?: string;
    bundles?: PricingBundle[];
  } | null;
  productData: {
    description?: string;
    ingredients?: string[];
    testimonials?: Array<{ text: string; author?: string }>;
    images?: Array<string | { url: string; alt?: string }>;
    videos?: Array<{ url: string; poster?: string; caption?: string }>;
    faqs?: Array<{ question: string; answer: string }>;
    contentSections?: Array<{ heading: string; body: string }>;
    enrichedContent?: {
      sections: Array<{ heading: string; body: string }>;
      generatedAt: string;
    };
    h1?: string;
    h2s?: string[];
  } | null;
}

export interface CategoryCount {
  category: string | null;
  count: number;
}

export interface CategoryWithImage {
  category: string | null;
  count: number;
  imageUrl: string | null;
  productName: string | null;
  productSlug: string | null;
}

// ─── Fetch helpers ──────────────────────────────────────────────────────────

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}

// ─── Hooks ──────────────────────────────────────────────────────────────────

export function useShopProducts() {
  return useQuery({
    queryKey: ["shop", "products"],
    queryFn: () => fetchJson<Record<string, Product[]>>("/api/pg/shop/products"),
  });
}

export function useShopProduct(slug: string) {
  return useQuery({
    queryKey: ["shop", "product", slug],
    queryFn: () =>
      fetchJson<{
        product: Product;
        assets: Asset[];
        blogPosts: BlogPost[];
        research: ProductResearch | null;
      }>(`/api/pg/shop/products/${encodeURIComponent(slug)}`),
    enabled: !!slug,
  });
}

export function useCategoryEnrichment(slug: string) {
  return useQuery({
    queryKey: ["shop", "category-enrichment", slug],
    queryFn: () =>
      fetchJson<{
        sections: Array<{ heading: string; body: string }>;
        generatedAt: string | null;
      }>(`/api/pg/shop/category-enrichment/${encodeURIComponent(slug)}`),
    enabled: !!slug,
  });
}

export function useShopCategories() {
  return useQuery({
    queryKey: ["shop", "categories"],
    queryFn: () => fetchJson<{ categories: CategoryCount[] }>("/api/pg/shop/categories"),
  });
}

export function useShopCategoriesWithImages() {
  return useQuery({
    queryKey: ["shop", "categories-with-images"],
    queryFn: () =>
      fetchJson<{ categories: CategoryWithImage[] }>("/api/pg/shop/categories-with-images"),
  });
}

export function useShopFeatured(limit = 8) {
  return useQuery({
    queryKey: ["shop", "featured", limit],
    queryFn: () =>
      fetchJson<{ products: Product[] }>(`/api/pg/shop/featured?limit=${limit}`),
  });
}

export interface MegaMenuProduct {
  name: string;
  slug: string;
  imageUrl: string | null;
  category: string | null;
}

export function useShopBlogPosts(limit = 3) {
  return useQuery({
    queryKey: ["shop", "blog-posts", limit],
    queryFn: () =>
      fetchJson<{
        posts: Array<{
          id: number;
          title: string;
          slug: string;
          heroImagePath: string | null;
          publishedAt: string | null;
          productName: string | null;
        }>;
      }>(`/api/pg/shop/blog-posts?limit=${limit}`),
  });
}

export function useShopMegaMenuFeatured() {
  return useQuery({
    queryKey: ["shop", "mega-menu-featured"],
    queryFn: () =>
      fetchJson<{ featured: Record<string, MegaMenuProduct[]> }>("/api/pg/shop/mega-menu-featured"),
  });
}
